/**
 * GlobalEditorPage — locale-aware editor for a single global.
 *
 * Route: /cms/globals/:slug
 *
 * Features:
 * - Locale tabs (ES / EN / PT) at the top
 * - Field rendering based on GLOBAL_FIELD_SCHEMAS[slug]
 * - Field types: text, textarea, number, richtext (Tiptap), array
 * - Nested object helpers for dotted paths (getNestedValue / setNestedValue)
 * - Save button calls useSaveGlobalLocale
 *
 * Schema design:
 * Each schema entry maps a field path (dot-separated for nesting) to a field definition.
 * Fields marked `localized: true` are stored per-locale in global_locales.data.
 * Non-localized fields are stored in globals.data (neutral).
 * The editor works with the `merged` object — locale_data wins on conflict.
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import type { JSONContent } from '@tiptap/react'
import { LocaleTabSwitcher, type SupportedLocale } from '@/components/cms/LocaleTabSwitcher'
import { TiptapEditor } from '@/components/cms/TiptapEditor'
import { useGlobalLocale, useSaveGlobalLocale, useSaveGlobalNeutral } from '@/hooks/useCms'

// ── Field schema types ────────────────────────────────────────────────────────

type FieldType = 'text' | 'textarea' | 'number' | 'richtext' | 'array'

interface FieldSchema {
  /** Dot-separated path into the data object, e.g. "hero.headline" */
  path: string
  label: string
  type: FieldType
  /** If true, field is locale-specific. If false, it's neutral (locale-agnostic). */
  localized: boolean
  /** For array fields: schema of each item (flat key -> label mapping) */
  itemFields?: { key: string; label: string; type: 'text' | 'number' }[]
  placeholder?: string
}

// ── Global field schemas ───────────────────────────────────────────────────────

const GLOBAL_FIELD_SCHEMAS: Record<string, FieldSchema[]> = {
  'site-texts': [
    { path: 'hero.headline', label: 'Hero — Título Principal', type: 'text', localized: true, placeholder: 'Se integra con tu POS actual' },
    { path: 'hero.subheadline', label: 'Hero — Subtítulo', type: 'textarea', localized: true },
    { path: 'hero.ctaPrimary', label: 'Hero — Botón Principal', type: 'text', localized: true, placeholder: 'Ver Precios' },
    { path: 'hero.ctaSecondary', label: 'Hero — Botón Secundario', type: 'text', localized: true, placeholder: 'Conocer Más' },
    { path: 'problemSolution.sectionLabel', label: 'Prob/Sol — Etiqueta de Sección', type: 'text', localized: true, placeholder: 'EL PROBLEMA' },
    { path: 'problemSolution.headline', label: 'Prob/Sol — Título', type: 'text', localized: true },
    { path: 'problemSolution.description', label: 'Prob/Sol — Descripción', type: 'richtext', localized: true },
    {
      path: 'problemSolution.painPoints',
      label: 'Prob/Sol — Puntos de Dolor',
      type: 'array',
      localized: true,
      itemFields: [{ key: 'text', label: 'Texto', type: 'text' }],
    },
    { path: 'problemSolution.tabletsTitle', label: 'Prob/Sol — Tablets Título', type: 'text', localized: true, placeholder: '4 tablets en tu cocina' },
    { path: 'problemSolution.tabletsDesc', label: 'Prob/Sol — Tablets Descripción', type: 'text', localized: true },
    { path: 'problemSolution.brandTitle', label: 'Prob/Sol — Marca Título', type: 'text', localized: true, placeholder: 'Tu cliente no te conoce' },
    { path: 'problemSolution.brandDesc', label: 'Prob/Sol — Marca Descripción', type: 'text', localized: true },
    { path: 'problemSolution.commissionsTitle', label: 'Prob/Sol — Comisiones Título', type: 'text', localized: true, placeholder: 'Comisiones de hasta 30%' },
    { path: 'problemSolution.commissionsDesc', label: 'Prob/Sol — Comisiones Descripción', type: 'text', localized: true },
    { path: 'problemSolution.delayed', label: 'Prob/Sol — Texto Atrasada', type: 'text', localized: true, placeholder: '¡Atrasada!' },
    { path: 'problemSolution.yourPOS', label: 'Prob/Sol — Tu POS', type: 'text', localized: true, placeholder: 'Tu POS' },
    { path: 'problemSolution.manual', label: 'Prob/Sol — Manual', type: 'text', localized: true, placeholder: 'Manual' },
    { path: 'problemSolution.typing', label: 'Prob/Sol — Digitando', type: 'text', localized: true, placeholder: 'Digitando...' },
    { path: 'problemSolution.noConnection', label: 'Prob/Sol — Sin Conexión', type: 'text', localized: true, placeholder: '3 sistemas · 0 conexión' },
    { path: 'problemSolution.notYourBrand', label: 'Prob/Sol — No Tu Marca', type: 'text', localized: true, placeholder: 'NO ES TU MARCA' },
    { path: 'problemSolution.yourProfit', label: 'Prob/Sol — Tu Ganancia', type: 'text', localized: true, placeholder: 'Tu ganancia' },
    { path: 'problemSolution.perSales', label: 'Prob/Sol — Por Ventas', type: 'text', localized: true, placeholder: 'Por cada $100.000 en ventas' },
    { path: 'finalCta.headline', label: 'CTA Final — Título', type: 'text', localized: true, placeholder: 'Listo para transformar tu restaurante?' },
    { path: 'finalCta.description', label: 'CTA Final — Descripción', type: 'richtext', localized: true },
    { path: 'finalCta.buttonText', label: 'CTA Final — Texto del Botón', type: 'text', localized: true, placeholder: 'Empezar Ahora' },
    { path: 'trustedBy.headline', label: 'Trusted By — Título', type: 'text', localized: true, placeholder: 'Integrado con las plataformas líderes en LATAM' },
    { path: 'testimonials.sectionLabel', label: 'Testimonios — Etiqueta', type: 'text', localized: true, placeholder: 'TESTIMONIOS' },
    { path: 'testimonials.headline', label: 'Testimonios — Título', type: 'text', localized: true, placeholder: 'Lo que dicen nuestros clientes' },
    { path: 'integrations.sectionLabel', label: 'Integraciones — Etiqueta', type: 'text', localized: true, placeholder: 'INTEGRACIONES' },
    { path: 'integrations.headline', label: 'Integraciones — Título', type: 'text', localized: true, placeholder: 'Conecta con todo tu ecosistema' },
  ],

  'social-proof': [
    { path: 'metrics.restaurants.count', label: 'Restaurantes — Cantidad', type: 'number', localized: false },
    { path: 'metrics.restaurants.prefix', label: 'Restaurantes — Prefijo', type: 'text', localized: false, placeholder: '+' },
    { path: 'metrics.restaurants.label', label: 'Restaurantes — Etiqueta', type: 'text', localized: true, placeholder: 'restaurantes' },
    { path: 'metrics.orders.count', label: 'Órdenes — Cantidad', type: 'text', localized: false, placeholder: '100K' },
    { path: 'metrics.orders.prefix', label: 'Órdenes — Prefijo', type: 'text', localized: false, placeholder: '+' },
    { path: 'metrics.orders.label', label: 'Órdenes — Etiqueta', type: 'text', localized: true, placeholder: 'órdenes procesadas' },
    { path: 'metrics.uptime.value', label: 'Uptime — Valor', type: 'text', localized: false, placeholder: '99.9%' },
    { path: 'metrics.uptime.label', label: 'Uptime — Etiqueta', type: 'text', localized: true, placeholder: 'uptime garantizado' },
    { path: 'metrics.satisfaction.value', label: 'Satisfacción — Valor', type: 'text', localized: false, placeholder: '4.9/5' },
    { path: 'metrics.satisfaction.label', label: 'Satisfacción — Etiqueta', type: 'text', localized: true, placeholder: 'satisfacción clientes' },
    { path: 'trustBadge.text', label: 'Badge de Confianza', type: 'text', localized: true, placeholder: 'Usado por +280 restaurantes en LATAM' },
  ],

  'contact-settings': [
    { path: 'info.email', label: 'Email Principal', type: 'text', localized: false, placeholder: 'hola@1klick.com' },
    { path: 'info.phone', label: 'Teléfono', type: 'text', localized: false },
    { path: 'info.whatsapp', label: 'WhatsApp', type: 'text', localized: false, placeholder: '+57 300 123 4567' },
    { path: 'info.address', label: 'Dirección', type: 'textarea', localized: true },
    { path: 'social.instagram', label: 'Instagram URL', type: 'text', localized: false },
    { path: 'social.linkedin', label: 'LinkedIn URL', type: 'text', localized: false },
    { path: 'social.twitter', label: 'Twitter/X URL', type: 'text', localized: false },
    { path: 'social.facebook', label: 'Facebook URL', type: 'text', localized: false },
    { path: 'social.youtube', label: 'YouTube URL', type: 'text', localized: false },
    { path: 'form.recipientEmail', label: 'Formulario — Email Destinatario', type: 'text', localized: false },
    { path: 'form.successMessage', label: 'Formulario — Mensaje de Éxito', type: 'text', localized: true, placeholder: '¡Gracias! Nos pondremos en contacto pronto.' },
    { path: 'schedule.weekdays', label: 'Horario — Lunes a Viernes', type: 'text', localized: false, placeholder: '9:00 AM - 6:00 PM' },
    { path: 'schedule.saturday', label: 'Horario — Sábado', type: 'text', localized: false, placeholder: '9:00 AM - 1:00 PM' },
    { path: 'schedule.sunday', label: 'Horario — Domingo', type: 'text', localized: false, placeholder: 'Cerrado' },
    { path: 'schedule.timezone', label: 'Horario — Zona Horaria', type: 'text', localized: false, placeholder: 'América/Bogotá (GMT-5)' },
  ],

  'hub-standalone': [
    { path: 'title', label: 'Título', type: 'text', localized: true, placeholder: 'HUB de Agregación Standalone' },
    { path: 'description', label: 'Descripción', type: 'textarea', localized: true },
    {
      path: 'tiers',
      label: 'Niveles de Precio',
      type: 'array',
      localized: false,
      itemFields: [
        { key: 'minOrders', label: 'Órdenes Mínimas', type: 'number' },
        { key: 'maxOrders', label: 'Órdenes Máximas', type: 'number' },
        { key: 'pricePerOrder', label: 'Precio por Orden', type: 'number' },
        { key: 'rangeDisplay', label: 'Rango a Mostrar', type: 'text' },
      ],
    },
  ],

  'virtual-queue': [
    { path: 'headline', label: 'Título', type: 'text', localized: true, placeholder: 'Tus clientes hacen fila' },
    { path: 'headlineHighlight', label: 'Título Highlight', type: 'text', localized: true, placeholder: 'desde su celular' },
    { path: 'description', label: 'Descripción', type: 'textarea', localized: true },
    { path: 'noApps', label: 'Sin Apps', type: 'text', localized: true, placeholder: 'Sin apps, sin descargas.' },
    { path: 'label', label: 'Label', type: 'text', localized: true, placeholder: 'Fila Virtual' },
    { path: 'yourTurn', label: 'Tu Turno', type: 'text', localized: true },
    { path: 'yourNumber', label: 'Tu Número', type: 'text', localized: true },
    { path: 'estimatedTime', label: 'Tiempo Estimado', type: 'text', localized: true },
    { path: 'status', label: 'Estado', type: 'text', localized: true },
    { path: 'waiting', label: 'En Espera', type: 'text', localized: true },
    { path: 'position', label: 'Posición', type: 'text', localized: true },
    { path: 'beforeYou', label: 'Antes de Ti', type: 'text', localized: true },
    { path: 'scanToJoin', label: 'Escanea para Entrar', type: 'text', localized: true },
    { path: 'dashboardStaff', label: 'Dashboard Staff', type: 'text', localized: true },
    { path: 'live', label: 'En Vivo', type: 'text', localized: true },
    { path: 'tableReady', label: 'Mesa Lista', type: 'text', localized: true },
    { path: 'goToWaiter', label: 'Ir al Mesero', type: 'text', localized: true },
    { path: 'businessImpact', label: 'Impacto en Negocio', type: 'text', localized: true },
    { path: 'complaints', label: 'Quejas', type: 'text', localized: true },
    { path: 'rotation', label: 'Rotación', type: 'text', localized: true },
    { path: 'satisfaction', label: 'Satisfacción', type: 'text', localized: true },
    { path: 'extraApps', label: 'Apps Extra', type: 'text', localized: true },
    { path: 'lessComplaints', label: 'Menos Quejas', type: 'text', localized: true },
    { path: 'waitTime', label: 'Tiempo de Espera', type: 'text', localized: true },
    { path: 'crowding', label: 'Aglomeraciones', type: 'text', localized: true },
    { path: 'atDoor', label: 'En la Puerta', type: 'text', localized: true },
    { path: 'tableRotation', label: 'Rotación Mesas', type: 'text', localized: true },
    { path: 'perService', label: 'Por Servicio', type: 'text', localized: true },
    { path: 'yourBrand', label: 'Tu Marca', type: 'text', localized: true },
    { path: 'noThirdParty', label: 'Sin Terceros', type: 'text', localized: true },
    { path: 'tableReadyNoWait', label: 'Mesa Lista Sin Esperar', type: 'text', localized: true },
    { path: 'directToSeat', label: 'Directo a Sentarse', type: 'text', localized: true },
    { path: 'capabilitiesTitle', label: 'Título Capacidades', type: 'text', localized: true },
    {
      path: 'capabilities',
      label: 'Capacidades',
      type: 'array',
      localized: true,
      itemFields: [{ key: 'text', label: 'Texto', type: 'text' }],
    },
    { path: 'wantVirtualQueue', label: 'CTA — Quiero Fila Virtual', type: 'text', localized: true },
    { path: 'includedInPlans', label: 'Incluido en Planes', type: 'text', localized: true },
  ],

  'features-showcase': [
    { path: 'headline', label: 'Título Principal', type: 'text', localized: true },
    { path: 'subheadline', label: 'Subtítulo', type: 'text', localized: true },
    { path: 'tableServiceBadge', label: 'Mesa — Badge', type: 'text', localized: true },
    { path: 'tableServiceTitle', label: 'Mesa — Título', type: 'text', localized: true },
    { path: 'tableServiceLead', label: 'Mesa — Lead', type: 'text', localized: true },
    { path: 'tableServiceDesc', label: 'Mesa — Descripción', type: 'textarea', localized: true },
    { path: 'whatsappBadge', label: 'WhatsApp — Badge', type: 'text', localized: true },
    { path: 'whatsappTitle', label: 'WhatsApp — Título', type: 'text', localized: true },
    { path: 'whatsappLead', label: 'WhatsApp — Lead', type: 'text', localized: true },
    { path: 'whatsappDesc', label: 'WhatsApp — Descripción', type: 'textarea', localized: true },
    { path: 'moreHeadline', label: 'Más Features — Título', type: 'text', localized: true },
    { path: 'moreSubheadline', label: 'Más Features — Subtítulo', type: 'text', localized: true },
    { path: 'hubTitle', label: 'HUB — Título', type: 'text', localized: true },
    { path: 'hubLead', label: 'HUB — Lead', type: 'text', localized: true },
    { path: 'hubDesc', label: 'HUB — Descripción', type: 'textarea', localized: true },
    { path: 'menuTitle', label: 'Menú — Título', type: 'text', localized: true },
    { path: 'menuLead', label: 'Menú — Lead', type: 'text', localized: true },
    { path: 'menuDesc', label: 'Menú — Descripción', type: 'textarea', localized: true },
    { path: 'kioskTitle', label: 'Kiosko — Título', type: 'text', localized: true },
    { path: 'kioskLead', label: 'Kiosko — Lead', type: 'text', localized: true },
    { path: 'kioskDesc', label: 'Kiosko — Descripción', type: 'textarea', localized: true },
    { path: 'qrTitle', label: 'QR — Título', type: 'text', localized: true },
    { path: 'qrLead', label: 'QR — Lead', type: 'text', localized: true },
    { path: 'qrDesc', label: 'QR — Descripción', type: 'textarea', localized: true },
    { path: 'landingTitle', label: 'Landing — Título', type: 'text', localized: true },
    { path: 'landingLead', label: 'Landing — Lead', type: 'text', localized: true },
    { path: 'landingDesc', label: 'Landing — Descripción', type: 'textarea', localized: true },
    { path: 'ecommerceTitle', label: 'Ecommerce — Título', type: 'text', localized: true },
    { path: 'ecommerceLead', label: 'Ecommerce — Lead', type: 'text', localized: true },
    { path: 'ecommerceDesc', label: 'Ecommerce — Descripción', type: 'textarea', localized: true },
    { path: 'posTitle', label: 'POS — Título', type: 'text', localized: true },
    { path: 'posDesc', label: 'POS — Descripción', type: 'textarea', localized: true },
    { path: 'reservationsTitle', label: 'Reservaciones — Título', type: 'text', localized: true },
    { path: 'reservationsDesc', label: 'Reservaciones — Descripción', type: 'textarea', localized: true },
    { path: 'chatTitle', label: 'Chat IA — Título', type: 'text', localized: true },
    { path: 'chatDesc', label: 'Chat IA — Descripción', type: 'textarea', localized: true },
  ],

  'pricing-texts': [
    { path: 'homeHeadline', label: 'Título Home Pricing', type: 'text', localized: true, placeholder: 'Planes simples para trabajo serio' },
    { path: 'perMonth', label: 'Por Mes Label', type: 'text', localized: true, placeholder: '/mes' },
    { path: 'basicDescription', label: 'Basic — Descripción', type: 'text', localized: true },
    { path: 'standardDescription', label: 'Standard — Descripción', type: 'text', localized: true },
    { path: 'advancedDescription', label: 'Advanced — Descripción', type: 'text', localized: true },
    { path: 'enterpriseDescription', label: 'Enterprise — Descripción', type: 'text', localized: true },
    { path: 'choosePlan', label: 'CTA — Elegir Plan', type: 'text', localized: true },
    { path: 'contactSales', label: 'CTA — Contactar Ventas', type: 'text', localized: true },
    { path: 'popular', label: 'Badge — Más Popular', type: 'text', localized: true },
    { path: 'save10', label: 'Badge — Ahorro', type: 'text', localized: true, placeholder: '-10%' },
    { path: 'flexible', label: 'Precio Flexible', type: 'text', localized: true, placeholder: 'Flexible' },
    { path: 'unlimited', label: 'Ilimitados', type: 'text', localized: true, placeholder: 'Ilimitados' },
    { path: 'ordersIncluded', label: 'Órdenes Incluidas', type: 'text', localized: true },
    { path: 'usersIncluded', label: 'Usuarios Incluidos', type: 'text', localized: true },
    { path: 'allInPrevious', label: 'Todo en (prefijo)', type: 'text', localized: true, placeholder: 'Todo en' },
    { path: 'viewAllPlans', label: 'Ver Todos los Planes', type: 'text', localized: true },
  ],

  'navigation': [
    { path: 'nav.pricing', label: 'Nav — Precios', type: 'text', localized: true },
    { path: 'nav.contact', label: 'Nav — Contacto', type: 'text', localized: true },
    { path: 'nav.startFree', label: 'Nav — CTA', type: 'text', localized: true, placeholder: 'Agendar Demo' },
    { path: 'nav.openMenu', label: 'Nav — Abrir Menú', type: 'text', localized: true },
    { path: 'nav.closeMenu', label: 'Nav — Cerrar Menú', type: 'text', localized: true },
    { path: 'footer.description', label: 'Footer — Descripción', type: 'textarea', localized: true },
    { path: 'footer.product', label: 'Footer — Producto', type: 'text', localized: true },
    { path: 'footer.company', label: 'Footer — Empresa', type: 'text', localized: true },
    { path: 'footer.contact', label: 'Footer — Contacto', type: 'text', localized: true },
    { path: 'footer.features', label: 'Footer — Características', type: 'text', localized: true },
    { path: 'footer.pricing', label: 'Footer — Precios', type: 'text', localized: true },
    { path: 'footer.terms', label: 'Footer — Términos', type: 'text', localized: true },
    { path: 'footer.privacy', label: 'Footer — Privacidad', type: 'text', localized: true },
    { path: 'footer.rights', label: 'Footer — Derechos', type: 'text', localized: true },
    { path: 'footer.pages', label: 'Footer — Páginas', type: 'text', localized: true },
  ],
}

const GLOBAL_DISPLAY_NAMES: Record<string, string> = {
  'site-texts': 'Textos del Sitio',
  'social-proof': 'Social Proof',
  'contact-settings': 'Configuración de Contacto',
  'hub-standalone': 'HUB Standalone',
  'virtual-queue': 'Fila Virtual',
  'features-showcase': 'Features Showcase',
  'pricing-texts': 'Textos de Pricing',
  'navigation': 'Navegación',
}

// ── Nested value helpers ───────────────────────────────────────────────────────

/**
 * Get a value at a dotted path from an object.
 * e.g. getNestedValue({ hero: { headline: 'Hi' } }, 'hero.headline') => 'Hi'
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  const keys = path.split('.')
  let current: unknown = obj
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined
    }
    current = (current as Record<string, unknown>)[key]
  }
  return current
}

/**
 * Return a new object with value set at the dotted path.
 * Immutable — creates new objects at every level.
 */
function setNestedValue(
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const keys = path.split('.')
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value }
  }

  const [head, ...rest] = keys
  const nested = (obj[head] as Record<string, unknown>) ?? {}
  return {
    ...obj,
    [head]: setNestedValue(nested, rest.join('.'), value),
  }
}

// ── Empty Tiptap document ──────────────────────────────────────────────────────

const EMPTY_DOC: JSONContent = { type: 'doc', content: [] }

function toJSONContent(value: unknown): JSONContent {
  if (value && typeof value === 'object' && 'type' in value) {
    return value as JSONContent
  }
  return EMPTY_DOC
}

// ── Array field editor ────────────────────────────────────────────────────────

interface ArrayFieldEditorProps {
  path: string
  label: string
  itemFields: NonNullable<FieldSchema['itemFields']>
  data: Record<string, unknown>
  onChange: (path: string, value: unknown) => void
}

function ArrayFieldEditor({ path, label, itemFields, data, onChange }: ArrayFieldEditorProps) {
  const items = (getNestedValue(data, path) as Record<string, unknown>[]) ?? []

  const updateItem = (index: number, key: string, value: unknown) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [key]: value } : item,
    )
    onChange(path, updated)
  }

  const addItem = () => {
    const newItem: Record<string, unknown> = {}
    for (const f of itemFields) {
      newItem[f.key] = f.type === 'number' ? 0 : ''
    }
    onChange(path, [...items, newItem])
  }

  const removeItem = (index: number) => {
    onChange(path, items.filter((_, i) => i !== index))
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500">Item {index + 1}</span>
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="text-xs text-red-600 hover:text-red-800"
              >
                Eliminar
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {itemFields.map((field) => (
                <div key={field.key}>
                  <label className="block text-xs text-gray-600 mb-1">{field.label}</label>
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={String(item[field.key] ?? '')}
                    onChange={(e) => {
                      const val = field.type === 'number'
                        ? (e.target.value === '' ? null : Number(e.target.value))
                        : e.target.value
                      updateItem(index, field.key, val)
                    }}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    step={field.type === 'number' ? 'any' : undefined}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={addItem}
          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          + Agregar item
        </button>
      </div>
    </div>
  )
}

// ── Field renderer ────────────────────────────────────────────────────────────

interface FieldRendererProps {
  field: FieldSchema
  data: Record<string, unknown>
  onChange: (path: string, value: unknown) => void
  locale: SupportedLocale
}

function FieldRenderer({ field, data, onChange, locale }: FieldRendererProps) {
  const value = getNestedValue(data, field.path)
  const isLocaleField = field.localized
  const localeLabel = isLocaleField ? ` [${locale.toUpperCase()}]` : ' [neutral]'

  if (field.type === 'array' && field.itemFields) {
    return (
      <ArrayFieldEditor
        path={field.path}
        label={`${field.label}${localeLabel}`}
        itemFields={field.itemFields}
        data={data}
        onChange={onChange}
      />
    )
  }

  if (field.type === 'richtext') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label}
          <span className="text-xs text-gray-400 ml-1">{localeLabel}</span>
        </label>
        <TiptapEditor
          value={toJSONContent(value)}
          onChange={(json) => onChange(field.path, json)}
          placeholder={field.placeholder}
        />
      </div>
    )
  }

  if (field.type === 'textarea') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label}
          <span className="text-xs text-gray-400 ml-1">{localeLabel}</span>
        </label>
        <textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(field.path, e.target.value)}
          placeholder={field.placeholder}
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 resize-vertical"
        />
      </div>
    )
  }

  if (field.type === 'number') {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label}
          <span className="text-xs text-gray-400 ml-1">{localeLabel}</span>
        </label>
        <input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => onChange(field.path, e.target.value === '' ? null : Number(e.target.value))}
          placeholder={field.placeholder}
          step="any"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
    )
  }

  // Default: text
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {field.label}
        <span className="text-xs text-gray-400 ml-1">{localeLabel}</span>
      </label>
      <input
        type="text"
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(field.path, e.target.value)}
        placeholder={field.placeholder}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function GlobalEditorPage() {
  const { slug } = useParams<{ slug: string }>()
  const [locale, setLocale] = useState<SupportedLocale>('es')
  const [localData, setLocalData] = useState<Record<string, unknown>>({})
  const [hasChanges, setHasChanges] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const { data: globalLocaleData, isLoading, error } = useGlobalLocale(slug ?? '', locale)
  const saveGlobalLocale = useSaveGlobalLocale()
  const saveGlobalNeutral = useSaveGlobalNeutral()

  // Sync local editor state when locale or slug changes
  useEffect(() => {
    if (globalLocaleData?.merged) {
      setLocalData(globalLocaleData.merged)
      setHasChanges(false)
      setSaveSuccess(false)
    }
  }, [globalLocaleData?.merged, slug, locale])

  const handleFieldChange = useCallback((path: string, value: unknown) => {
    setLocalData((prev) => setNestedValue(prev, path, value))
    setHasChanges(true)
    setSaveSuccess(false)
  }, [])

  const handleLocaleChange = (newLocale: SupportedLocale) => {
    setLocale(newLocale)
    setHasChanges(false)
    setSaveSuccess(false)
  }

  const handleSave = async () => {
    if (!slug) return

    // Split localData into locale-specific fields and neutral fields
    // based on the schema definition for this slug
    const schemas = GLOBAL_FIELD_SCHEMAS[slug] ?? []
    const localeSpecificData: Record<string, unknown> = {}
    const neutralData: Record<string, unknown> = {}

    for (const field of schemas) {
      const value = getNestedValue(localData, field.path)
      if (field.localized) {
        // Set into locale-specific object
        const target = setNestedValue(localeSpecificData, field.path, value)
        Object.assign(localeSpecificData, target)
      } else {
        // Set into neutral object
        const target = setNestedValue(neutralData, field.path, value)
        Object.assign(neutralData, target)
      }
    }

    try {
      // Save neutral (locale-agnostic) data if any
      if (Object.keys(neutralData).length > 0) {
        await saveGlobalNeutral.mutateAsync({ slug, data: neutralData })
      }
      // Save locale-specific data to global_locales
      if (Object.keys(localeSpecificData).length > 0) {
        await saveGlobalLocale.mutateAsync({
          slug,
          locale,
          data: localeSpecificData,
        })
      }
      setHasChanges(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      console.error('[GlobalEditorPage] Save error:', err)
    }
  }

  const displayName = slug ? (GLOBAL_DISPLAY_NAMES[slug] ?? slug) : 'Global'
  const schemas = slug ? (GLOBAL_FIELD_SCHEMAS[slug] ?? []) : []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-gray-500">Cargando editor...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-700">Error al cargar el global: {error.message}</p>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Breadcrumb */}
      <nav className="mb-4 text-sm text-gray-500">
        <Link to="/cms/globals" className="hover:text-gray-700">
          CMS
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">{displayName}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{displayName}</h1>
          <p className="text-xs text-gray-400 font-mono mt-0.5">{slug}</p>
        </div>

        <div className="flex items-center gap-3">
          {saveSuccess && (
            <span className="text-sm text-green-600 font-medium">Guardado</span>
          )}
          {saveGlobalLocale.isError && (
            <span className="text-sm text-red-600">
              Error: {saveGlobalLocale.error?.message}
            </span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || saveGlobalLocale.isPending || saveGlobalNeutral.isPending}
            className={[
              'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
              hasChanges && !saveGlobalLocale.isPending || saveGlobalNeutral.isPending
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed',
            ].join(' ')}
          >
            {saveGlobalLocale.isPending || saveGlobalNeutral.isPending ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* Locale tabs */}
      <div className="mb-6">
        <LocaleTabSwitcher locale={locale} onChange={handleLocaleChange} />
        {hasChanges && (
          <p className="text-xs text-amber-600 mt-2">Cambios sin guardar en {locale.toUpperCase()}</p>
        )}
      </div>

      {/* Fields */}
      <div className="space-y-6 bg-white border border-gray-200 rounded-lg p-6">
        {schemas.length === 0 ? (
          <p className="text-gray-500 text-sm">No hay campos definidos para este global.</p>
        ) : (
          schemas.map((field) => (
            <FieldRenderer
              key={`${field.path}-${locale}`}
              field={field}
              data={localData}
              onChange={handleFieldChange}
              locale={locale}
            />
          ))
        )}
      </div>

      {/* Footer save */}
      {schemas.length > 0 && (
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || saveGlobalLocale.isPending || saveGlobalNeutral.isPending}
            className={[
              'px-6 py-2 text-sm font-medium rounded-lg transition-colors',
              hasChanges && !saveGlobalLocale.isPending || saveGlobalNeutral.isPending
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed',
            ].join(' ')}
          >
            {saveGlobalLocale.isPending || saveGlobalNeutral.isPending ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      )}
    </div>
  )
}
